import uniq from 'lodash/uniq';
import {
  BROWSER_ITEM_TYPES,
  MEDIAPANE_KEYS,
  MEDIA_KEYS,
} from '../constants/WbMediaConstants';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { buildApiUri } from '@sonifi/wb-core/utils/parseHelper';

export const bytesToHumanReadable = (input) => {
  if (!input) {
    return;
  }
  let units = 'B';
  let retval = input;

  if (retval >= 1024) {
    units = 'KB';
    retval = retval / 1024;
  }
  if (retval >= 1024) {
    units = 'MB';
    retval = retval / 1024;
  }
  if (retval >= 1024) {
    units = 'GB';
    retval = retval / 1024;
  }
  return retval.toFixed(1) + ' ' + units;
};

export const readableMIME = (input) =>
  uniq(input.replace(/( *[\w]*[/](?:x-)?)*/g, '').split(',')).join(', ');

export const thumbnailExists = (mediaItem) => {
  if (!mediaItem) {
    return false;
  }

  return !(
    (mediaItem.file_uri && mediaItem.file_uri?.indexOf('None') !== -1) ||
    mediaItem.file_uri === '' ||
    typeof mediaItem.thumbnail_pattern === 'undefined' ||
    mediaItem.thumbnail_pattern === null
  );
};

const padNumberMath = (number, pad) => {
  const N = Math.pow(10, pad);
  return number < N ? ('' + (N + number)).slice(1) : '' + number;
};

const mediaTooltipAudioVideo = (mediaItem) => {
  if (
    (mediaItem.type === MEDIA_KEYS.TYPE.VIDEOS.KEY ||
      mediaItem.type === MEDIA_KEYS.TYPE.AUDIOS.KEY) &&
    mediaItem.runtime &&
    mediaItem.runtime !== -1
  ) {
    const minutes = padNumberMath(Math.floor(mediaItem.runtime / 60), 1);
    const seconds = padNumberMath(mediaItem.runtime - Number(minutes) * 60, 2);
    return `${minutes}:${seconds}`;
  }

  return '';
};

export const mediaTooltip = (mediaItem) =>
  new Promise((resolve) => {
    if (mediaItem.type === MEDIA_KEYS.TYPE.VIDEOS.KEY) {
      const tooltip = mediaTooltipAudioVideo(mediaItem);
      resolve(tooltip);
    }
    if (
      mediaItem.type === MEDIA_KEYS.TYPE.IMAGES.KEY ||
      mediaItem.type === MEDIA_KEYS.TYPE.SLIDES.KEY
    ) {
      const image = new Image();
      image.src = mediaItem.file_uri;
      image.onload = () => {
        const tooltip = `${image.naturalWidth}px x ${image.naturalHeight}px`;
        resolve(tooltip);
      };
      image.onerror = () => {
        resolve('');
      };
    }
  });

export const getMediaItemMapped = ({
  item,
  itemEmbedded = {},
  mediaKey,
  size,
}) => {
  const finalItem =
    mediaKey === MEDIA_KEYS.TYPE.IMAGES.KEY ||
    mediaKey === MEDIA_KEYS.TYPE.SLIDES.KEY
      ? { ...item, thumbnail_pattern: itemEmbedded.thumbnail_pattern }
      : { ...item };
  let showThumb = false;
  let thumbnail = null;

  if (thumbnailExists(finalItem)) {
    showThumb = true;

    if (finalItem.thumbnail_pattern) {
      thumbnail = finalItem.thumbnail_pattern
        .replace('{WIDTH}', size.width)
        .replace('{HEIGHT}', size.height);
      if (
        mediaKey === MEDIA_KEYS.TYPE.IMAGES.KEY ||
        mediaKey === MEDIA_KEYS.TYPE.SLIDES.KEY
      ) {
        const tumb = finalItem.thumbnail_pattern
          .replace('{WIDTH}', '1280')
          .replace('{HEIGHT}', '720');
        finalItem.tumb = tumb;
      }
    }
  } else {
    thumbnail = itemEmbedded?.file_uri;
  }

  return {
    ...finalItem,
    file_uri: itemEmbedded?.file_uri,
    type: mediaKey,
    showThumb,
    thumbnail,
    typeAccept:
      itemEmbedded?.mimetype ??
      MEDIA_KEYS.TYPE[mediaKey.toUpperCase()]?.DRAGTYPE,
  };
};

export const getMediaMapped = ({ group, media, mediaItemsRes }) => {
  const mediaItemsMapped = mediaItemsRes.objects
    .filter((item) => item.uri.includes(media.toLowerCase()))
    .map((item) => {
      if (media === MEDIA_KEYS.TYPE.SERVICES.KEY) {
        return {
          ...item,
          file_uri: item.thumbnail,
          type: media,
          showThumb: true,
          group,
        };
      }

      const filesEmbedded =
        item.files?.map((fileUri) => mediaItemsRes._embedded[fileUri]) || [];
      const bestMimetype = MEDIA_KEYS.MIME[media.toUpperCase()].find(
        (mimetype) => filesEmbedded.some((file) => file?.mimetype === mimetype),
      );
      const itemEmbedded =
        filesEmbedded.find((file) => file?.mimetype === bestMimetype) ||
        (filesEmbedded.length > 0 ? filesEmbedded[0] : null);

      const res = getMediaItemMapped({
        item,
        itemEmbedded,
        mediaKey: media,
        size: {
          width: MEDIAPANE_KEYS.THUMBNAIL_SIZE.WIDTH,
          height: MEDIAPANE_KEYS.THUMBNAIL_SIZE.HEIGHT,
        },
      });

      return {
        ...res,
        group,
      };
    });

  return mediaItemsMapped;
};

export const getMediaItemPage = ({
  pagination,
  pageFromParams,
  currentPage = 0,
}) => {
  if (pageFromParams === 1) {
    return {
      canNext: true,
      nextPage: pageFromParams,
    };
  }
  const canNext =
    !currentPage && !pagination.total
      ? true
      : 10 * currentPage < pagination.total;
  const nextPage = canNext ? currentPage + 1 : pagination.page;
  return {
    canNext,
    nextPage,
  };
};

// API Helper Functions
export const createApiRequestParams = ({
  page,
  pageSize,
  term,
  selectedGroup,
  folderNode,
  defaultConfig,
  media,
}) => {
  // Specify all media types for the 'Media All' option
  const media_types =
    media === MEDIA_KEYS.TYPE.MEDIA.KEY
      ? [
          MEDIA_KEYS.TYPE.IMAGES.KEY,
          MEDIA_KEYS.TYPE.VIDEOS.KEY,
          MEDIA_KEYS.TYPE.AUDIOS.KEY,
          MEDIA_KEYS.TYPE.SLIDES.KEY,
        ]
      : undefined;

  return {
    page,
    rpp: pageSize,
    title__contains: term || undefined,
    _order_by: 'title',
    group: selectedGroup.uri,
    tags: !term
      ? buildApiUri(defaultConfig.apiUri, ENDPOINTS.TAGS, folderNode.id)
      : undefined,
    media_types,
  };
};

// Build media tree nodes for the given items, skipping any whose id (the
// item's uri) is already in the tree so a re-fetched page (e.g. a duplicate
// scroll trigger) cannot append duplicate, same-id nodes.
const buildNewMediaNodes = ({ mediaItemsMapped, browserTree, parentId }) => {
  const existingIds = new Set(browserTree.map((nodeTree) => nodeTree.id));
  return mediaItemsMapped
    .filter((item) => !existingIds.has(item.uri))
    .map((item) => ({
      id: item.uri,
      parent: parentId,
      text: item.title,
      data: item,
      type: 'media',
    }));
};

export const processMediaItemsForBrowserTree = ({
  mediaItemsMapped,
  term,
  browserTree,
  folderNode,
}) => {
  const updatedBrowserTree = [];

  if (term) {
    // For search results, add all items to root level.
    updatedBrowserTree.push(...browserTree);
    updatedBrowserTree.push(
      ...buildNewMediaNodes({ mediaItemsMapped, browserTree, parentId: 0 }),
    );
  } else {
    // For folder browsing, add items under their parent folder
    for (const folder of browserTree) {
      if (folder.id === folderNode.id) {
        updatedBrowserTree.push(
          ...buildNewMediaNodes({
            mediaItemsMapped,
            browserTree,
            parentId: folder.id,
          }),
        );
      }
      updatedBrowserTree.push(folder);
    }
  }

  return updatedBrowserTree;
};

export const getMediaKeysForType = (mediaType) =>
  mediaType === MEDIA_KEYS.TYPE.MEDIA.KEY
    ? [
        MEDIA_KEYS.TYPE.IMAGES.KEY.toUpperCase(),
        MEDIA_KEYS.TYPE.VIDEOS.KEY.toUpperCase(),
        MEDIA_KEYS.TYPE.AUDIOS.KEY.toUpperCase(),
        MEDIA_KEYS.TYPE.SLIDES.KEY.toUpperCase(),
      ]
    : [mediaType.toUpperCase()];

export const processMediaByType = ({
  groups,
  groupId,
  mediaKeys,
  mediaByTags,
}) => {
  const mediaItemsMapped = [];
  const group = groups.find((g) => g.id === groupId);

  for (const mediaKey of mediaKeys) {
    const mediaItems = getMediaMapped({
      group,
      media: mediaKey.toLowerCase(),
      mediaItemsRes: mediaByTags,
    });
    mediaItemsMapped.push(...mediaItems);
  }

  return mediaItemsMapped;
};

// Upload Helper Functions
export const createMediaUploadBody = (file, folderNode, defaultConfig) => ({
  files: [file.uri],
  title: file.title,
  group: file.group,
  tags: [buildApiUri(defaultConfig.apiUri, ENDPOINTS.TAGS, folderNode.data.id)],
});

export const prepareFilesForUpload = (files) =>
  Array.from(files.accepted).map((f) => ({
    name: f.name,
    loaded: false,
  }));

export const createUploadOptions = (mediaType) => ({
  fileExts: mediaType.accept?.split(',').map((mt) => mt.trim()) || [],
  maxFileSize: mediaType.maxFileSize,
  mimeTypes: mediaType.uploadMime,
});

// Tag Helper Functions
export const createTagBody = ({
  tagName,
  parentName,
  parentId,
  group,
  defaultConfig,
}) => ({
  tag: `${parentName}/${tagName.toLowerCase()}`,
  group: group.uri,
  tag_tags: [buildApiUri(defaultConfig.apiUri, ENDPOINTS.TAGS, parentId)],
});

export const createRootTagBody = ({ newTagName, group, rootTag }) => ({
  tag: `${rootTag}/${newTagName}`,
  group: group.uri,
});

export const createEditTagBody = ({ currentFolderName, newTagName }) => {
  const basePath = currentFolderName.endsWith('/')
    ? currentFolderName
    : currentFolderName.replace(/[^/]+$/, '');
  const newTag = `${basePath}${newTagName.toLowerCase()}`;

  return { tag: newTag };
};

// Move Operations Helper Functions
export const createMoveMediaBody = (folderTarget, defaultConfig) => ({
  tags: [buildApiUri(defaultConfig.apiUri, ENDPOINTS.TAGS, folderTarget.id)],
});

export const createMoveFolderBody = ({
  folderSource,
  folderTarget,
  defaultConfig,
  endpoints,
}) => ({
  tag: `${folderTarget.data.name}/${folderSource.text.toLowerCase()}`,
  tag_tags: [buildApiUri(defaultConfig.apiUri, endpoints.TAGS, folderTarget.id)],
});

export const updateBrowserTreeItemParent = ({
  browserTree,
  source,
  folderTarget,
}) => {
  try {
    const tree = structuredClone(browserTree);
    const parentFolderSource = tree.find((i) => i.id === source.parent);

    return tree.map((item) => {
      if (item.id === source.id) {
        return { ...item, parent: folderTarget.id };
      }
      if (
        item.type === BROWSER_ITEM_TYPES.FOLDER &&
        item.id === parentFolderSource.id
      ) {
        return {
          ...item,
          data: {
            ...item.data,
            children: item.data.children?.filter(
              (child) => child.id !== source.id,
            ),
          },
        };
      }
      return item;
    });
  } catch (error) {
    return browserTree;
  }
};
